// Planned items: the category screen, the item editor sheet, and the Item picker in the edit
// sheet. Statuses come from the server (computed for the month); the PWA only renders them.
// Transfer items (money set aside) show Done buttons instead of waiting for an alert.
import { fundUsedBlock } from './savings';
import type { CategoryDto, ItemDto, MoveItemDto, OverviewDto, TransactionDto } from '../../../shared/api';
import { dayLabel, esc, isoDay, money, plainMoney, shortDate } from '../format';
import { countsThroughText, dateText, nextOccurrence, yearlySummary } from '../../../shared/yearly';
import { txButton } from './activity';
import { detailBar } from './header';
import { vMonthBanner } from './month';
import { centsField } from '../cents';
import { doneSplitBlock, grossFor } from './splits';
import { splitsEnabled } from '../../../shared/partner';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Paid $182.70", "Due Oct 22", "Due any day", "Not seen yet", "Not this month". Transfers:
 * "Done $580", "Done $600 of $580" (over the plan), "Partly done $290 of $580".
 */
export function itemStatus(i: ItemDto): { text: string; cls: string } {
  const m = i.month;
  if (!m) return { text: 'Archived', cls: 'muted' };
  if (i.kind === 'transfer' && m.paidCents !== null && (m.status === 'due' || m.status === 'late')) {
    return { text: `Partly done ${money(m.paidCents, 2)} of ${money(m.expectedCents, 2)}`, cls: m.status === 'late' ? 'ahead' : '' };
  }
  switch (m.status) {
    case 'paid':
      if (i.kind === 'transfer' && m.expectedCents > 0 && (m.paidCents ?? 0) !== m.expectedCents) {
        return { text: `Done ${money(m.paidCents ?? 0, 2)} of ${money(m.expectedCents, 2)}`, cls: 'ok' };
      }
      return { text: `${i.kind === 'transfer' ? 'Done' : 'Paid'} ${money(m.paidCents ?? 0, 2)}`, cls: 'ok' };
    case 'due':
      return { text: m.dueDate ? `Due ${shortDate(m.dueDate)}` : 'Due any day', cls: '' };
    case 'late':
      return { text: 'Not seen yet', cls: 'ahead' };
    case 'not-this-month':
      return { text: 'Not this month', cls: 'muted' };
  }
}

/**
 * A yearly item's two lines on the Budget tab and the category screen: "Month 8 of 12, $15.00 a
 * month" (a spread item this month) and "Counts through Feb 2027", "Counted through Feb 2027" or
 * "Renews Mar 27 2027". Empty strings when they do not apply.
 */
export function yearlyLines(i: ItemDto, currentMonth: string): { term: string; when: string } {
  if (i.cadence !== 'yearly') return { term: '', when: '' };
  const sp = i.month?.spread;
  const when = i.countsThrough ? countsThroughText(i.countsThrough, currentMonth) : i.renewsOn ? `Renews ${dateText(i.renewsOn)}` : '';
  return { term: sp ? `Month ${sp.index} of 12, ${money(sp.shareCents, 2)} a month` : '', when };
}

/**
 * A spread item's share line in Activity, on the 1st of each month of a paid term:
 * "Pliability, $15.00 of your yearly $180 (paid Mar 15 2026). Month 5 of 12".
 */
export const shareText = (l: { name: string; shareCents: number; annualCents: number; paidOn: string | null; index: number }) =>
  `${l.name}, ${money(l.shareCents, 2)} of your yearly ${plainMoney(l.annualCents)}${l.paidOn ? ` (paid ${dateText(l.paidOn)})` : ''}. Month ${l.index} of 12`;

/** "Every month on the 22nd", "Jan, Apr, Jul, Oct on the 15th", "Every March". */
export function repeatsText(i: Pick<ItemDto, 'cadence' | 'months' | 'dueDay'>): string {
  const day = i.dueDay ? ` on day ${i.dueDay}` : '';
  if (i.cadence === 'monthly') return `Every month${day}`;
  const names = i.months.map((m) => MONTHS[m - 1]).join(', ');
  return i.cadence === 'yearly' ? `Every ${names}${day}` : `${names}${day}`;
}

/** Where a purchase can be filed: spending categories only (never money set aside). */
export const spendingCategories = (d: OverviewDto) => d.categories.filter((k) => k.kind !== 'set_aside');

export const categoryItems = (d: OverviewDto, categoryId: string) => d.items.filter((i) => i.categoryId === categoryId && !i.archived);

/** "3 of 6 paid", or "1 of 3 done" for money set aside. */
export const paidLine = (k: CategoryDto) => `${k.paidCount} of ${k.itemCount} ${k.kind === 'set_aside' ? 'done' : 'paid'}`;

/** "Due Oct 22", "Not seen yet", or "Due this month" for an any-day transfer; "Partly done $290 of $580" first when started. */
const moveDue = (m: Pick<MoveItemDto, 'status' | 'dueDate' | 'doneCents' | 'plannedCents'>) => {
  const when = m.status === 'late' ? 'Not seen yet' : m.dueDate ? `Due ${shortDate(m.dueDate)}` : 'Due this month';
  return m.doneCents > 0 ? `Partly done ${money(m.doneCents, 2)} of ${money(m.plannedCents, 2)}. ${when}` : when;
};

/**
 * Done, and "Done for a different amount", on a transfer item or (itemId null) on a plain
 * category of money set aside. Never a prompt, a Review item or an insight.
 */
const doneButtons = (itemId: string | null, categoryId?: string) =>
  itemId
    ? `<button class="btn primary" data-a="item-done" data-id="${esc(itemId)}">Done</button><button class="btn" data-a="item-done-other" data-id="${esc(itemId)}">Different amount</button>`
    : `<button class="btn primary" data-a="item-done" data-cat="${esc(categoryId!)}">Done</button><button class="btn" data-a="item-done-other" data-cat="${esc(categoryId!)}">Different amount</button>`;

/** "Mark not done": removes the latest entry (Undo brings it back). */
const notDoneButton = (itemId: string | null, categoryId?: string) =>
  `<button class="btn" data-a="item-not-done" ${itemId ? `data-id="${esc(itemId)}"` : `data-cat="${esc(categoryId!)}"`}>Mark not done</button>`;

/** The category's note under its screen's header, with the way to edit it. */
const noteBlock = (k: CategoryDto) =>
  k.note ? `<section class="catnote"><p>${esc(k.note)}</p><button class="linkbtn" data-a="cat-drawer" data-id="${esc(k.id)}">Edit category</button></section>` : '';

/**
 * "Move your money": transfers whose due day has come and that are not done yet, each with Done.
 * On Overview it has "Not now" (back in 3 days); on a category screen it lists that category's.
 */
export function moveCard(items: MoveItemDto[], opts: { snooze: boolean }): string {
  if (!items.length) return '';
  return `<section class="ask movecard"><h2>Move your money</h2><p class="hint">Transfers you make yourself. Tap Done once the money has moved.</p>
  ${items
    .map(
      (m) => `<div class="askrow"><div class="askhead"><span>${esc(m.name)}</span><span>${money(m.amountCents, 2)}</span></div>
  <p class="hint">${esc(moveDue(m))}</p><div class="acts">${doneButtons(m.itemId, m.categoryId)}</div></div>`,
    )
    .join('')}
  ${opts.snooze ? '<div class="acts"><button class="btn" data-a="move-snooze">Not now</button></div>' : ''}</section>`;
}

/** A transfer item on its category screen: tap the name to edit; Done or Mark not done beside it. */
function transferRow(i: ItemDto): string {
  const st = itemStatus(i);
  // The amount still to move while it is open; what was moved once it is done.
  const m = i.month;
  const amount = m?.status === 'paid' ? (m.paidCents ?? 0) : m && (m.status === 'due' || m.status === 'late') ? Math.max(0, m.expectedCents - (m.paidCents ?? 0)) : m?.expectedCents || i.amountCents;
  // Done moves what is left; Different amount adds any amount; Mark not done removes the latest entry.
  const notDone = notDoneButton(i.id);
  const started = (m?.paidCents ?? 0) > 0;
  const act =
    m?.status === 'paid'
      ? `${notDone}<button class="btn" data-a="item-done-other" data-id="${esc(i.id)}">Different amount</button>`
      : m && (m.status === 'due' || m.status === 'late')
        ? `${doneButtons(i.id)}${started ? notDone : ''}`
        : '';
  return `<div class="trow"><div class="iwrap"><button class="item" data-a="item-edit" data-id="${esc(i.id)}">
    <span class="itop"><span class="iname">${esc(i.name)}</span><span class="iamt">${money(amount, 2)}</span></span>
    <span class="ibot"><span class="st ${st.cls}">${esc(st.text)}</span><span class="rem">${esc(repeatsText(i))}</span></span>
    ${i.note ? `<span class="inote">${esc(i.note)}</span>` : ''}</button>${plusButton(i)}</div>${act ? `<div class="acts">${act}</div>` : ''}</div>`;
}

/** Dollars with cents only when there are some: "$250", "$17.50". */
const whole = (c: number) => money(c, c % 100 ? 2 : 0);

/**
 * One tap, today, with Undo, while nothing is logged to a one-amount spending category this month:
 * "Paid $250" with "Same amount every month" on, else "Log planned amount ($250)" (category
 * screen only). Nothing for a list, money set aside or a $0 plan.
 */
export function oneTapLog(k: CategoryDto, where: 'row' | 'screen'): string {
  if (k.kind === 'set_aside' || k.itemized || k.budgetCents <= 0 || k.spentCents !== 0) return '';
  if (k.sameAmount) return `<button class="btn paid" data-a="log-planned" data-cat="${esc(k.id)}">Paid ${whole(k.budgetCents)}</button>`;
  return where === 'screen' ? `<button class="btn" data-a="log-planned" data-cat="${esc(k.id)}">Log planned amount (${whole(k.budgetCents)})</button>` : '';
}

/** The category screen's way to the drawer (name, plan mode, amount, group, delete). */
const editButton = (k: CategoryDto) => `<button class="btn" data-a="cat-drawer" data-id="${esc(k.id)}">Edit category</button>`;

/** Why a category cannot be deleted (also the server's 409 message). */
export const CATEGORY_IN_USE_TEXT = 'It still has purchases or items. Move or delete them first, then delete the category.';

/**
 * The small "+" on an item row: log a purchase for this item (the add sheet, prefilled with its
 * category and the item, amount empty), or for a transfer the "Different amount" sheet.
 */
export const plusButton = (i: ItemDto) =>
  i.archived
    ? ''
    : i.kind === 'transfer'
      ? `<button class="iplus" data-a="item-done-other" data-id="${esc(i.id)}" aria-label="Log money moved for ${esc(i.name)}">+</button>`
      : `<button class="iplus" data-a="item-log" data-id="${esc(i.id)}" aria-label="Log a purchase for ${esc(i.name)}">+</button>`;

function itemRow(i: ItemDto, currentMonth: string): string {
  const st = itemStatus(i);
  const amount = i.month?.status === 'paid' ? i.month.paidCents ?? 0 : i.month?.expectedCents || i.amountCents;
  // A yearly item: two lines, "Month 8 of 12, $15.00 a month" and "Counts through Feb 2027" (or
  // "Renews Mar 27 2027"). An unpaid share also says when it is due.
  const y = yearlyLines(i, currentMonth);
  const first = i.ended ? y.when : y.term ? (i.month?.status === 'paid' ? y.term : `${y.term}. ${st.text}`) : st.text;
  const second = i.cadence === 'yearly' ? (i.ended ? `${plainMoney(i.amountCents)} a year` : y.when || `${plainMoney(i.amountCents)} a year`) : `${repeatsText(i)}${i.expectedMode === 'last_month' ? ", last month's amount" : ''}`;
  return `<div class="iwrap"><button class="item" data-a="item-edit" data-id="${esc(i.id)}">
    <span class="itop"><span class="iname">${esc(i.name)}</span><span class="iamt">${money(amount, 2)}</span></span>
    <span class="ibot"><span class="st ${i.ended ? 'muted' : st.cls}">${esc(first)}</span><span class="rem">${esc(second)}</span></span>
    ${i.note ? `<span class="inote">${esc(i.note)}</span>` : ''}</button>${i.ended ? '' : plusButton(i)}</div>`;
}

/** The category screen: name, spent, planned, left, then its purchases (plain) or its items (itemized). */
export function vCategory(d: OverviewDto, id: string): string {
  const k = d.categories.find((x) => x.id === id);
  if (!k) return `${detailBar('Category', '')}<p class="empty">That category is gone.</p>`;
  const txs = d.transactions.filter((t) => t.categoryId === k.id).sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
  const spentAll = k.spentCents;
  const left = k.budgetCents - spentAll;
  const head = `${detailBar(k.name, k.itemized ? paidLine(k) : 'One amount for the month')}${vMonthBanner(d)}
  <section class="hero small"><div class="nums">
    <div><p class="lab">Spent</p><p class="mid">${money(spentAll)}</p></div>
    <div><p class="lab">Planned</p><p class="mid">${money(k.budgetCents)}</p></div>
    <div><p class="lab">Left</p><p class="mid ${left < 0 ? 'neg' : ''}">${money(left)}</p></div>
  </div></section>
  ${noteBlock(k)}`;
  const actions = (extra: string) => `<div class="acts">${extra}<button class="btn" data-a="filter" data-cat="${esc(k.id)}">See activity</button>${editButton(k)}</div>`;
  const list = (rows: TransactionDto[]) =>
    rows.length ? `<div class="txg">${rows.map((t) => txButton(t, dayLabel(t.date))).join('')}</div>` : '<p class="empty">No purchases yet this month.</p>';

  if (k.kind === 'set_aside') return setAsideCategory(d, k, txs, list);
  if (!k.itemized) return `${head}${actions(oneTapLog(k, 'screen'))}<h2>This month</h2>${list(txs)}`;

  const items = categoryItems(d, k.id);
  const itemIds = new Set(d.items.filter((i) => i.categoryId === k.id).map((i) => i.id));
  const other = txs.filter((t) => !t.itemId || !itemIds.has(t.itemId));
  const archived = d.items.filter((i) => i.categoryId === k.id && i.archived);
  return `${head}
  ${actions(`<button class="btn primary" data-a="item-add" data-cat="${esc(k.id)}">Add item</button>`)}
  <h2>Items</h2>
  ${items.length ? `<div class="list">${items.map((i) => itemRow(i, d.month.key)).join('')}</div>` : '<p class="empty">No items yet. Add the things you pay for here.</p>'}
  ${k.extraCents > 0 ? `<div class="brow extra"><span>Room for other spending</span><span>${money(k.extraCents, 2)}</span></div>` : ''}
  <h2>Other</h2><p class="hint">Purchases in ${esc(k.name)} that are not one of its items.</p>${list(other)}
  ${archived.length ? `<details class="more"><summary>Archived (${archived.length})</summary><div class="list">${archived.map((i) => itemRow(i, d.month.key)).join('')}</div></details>` : ''}`;
}

/** "Which category is it in?" for a monthly charge whose merchant has none yet: spending categories only. */
export function vCandidateCategory(d: OverviewDto, candidateId: string, name: string): string {
  return `<div class="grab"></div><h2>Which category is ${esc(name)} in?</h2>
  <p class="hint">Hush remembers it, so its charges file themselves from now on.</p>
  <div class="chips">${spendingCategories(d)
    .map((k) => `<button class="chip" data-a="confirm-candidate" data-id="${esc(candidateId)}" data-cat="${esc(k.id)}">${esc(k.name)}</button>`)
    .join('')}</div>`;
}

/** The empty state of a set-aside category's entries. */
export const NOTHING_MOVED = 'Nothing moved yet this month.';

/** Money set aside: moved this month against planned, its transfers with Done, no extra amount. */
function setAsideCategory(d: OverviewDto, k: CategoryDto, txs: TransactionDto[], list: (rows: TransactionDto[]) => string): string {
  const left = Math.max(0, k.budgetCents - k.spentCents);
  const head = `${detailBar(k.name, k.itemized ? paidLine(k) : 'Money set aside')}${vMonthBanner(d)}
  <section class="hero small"><div class="nums">
    <div><p class="lab">Moved this month</p><p class="mid">${money(k.spentCents)}</p></div>
    <div><p class="lab">Planned</p><p class="mid">${money(k.budgetCents)}</p></div>
    <div><p class="lab">Left to move</p><p class="mid">${money(left)}</p></div>
  </div></section>
  ${fundUsedBlock(d, k)}${noteBlock(k)}`;
  const actions = `<div class="acts">${k.itemized ? `<button class="btn primary" data-a="item-add" data-cat="${esc(k.id)}">Add transfer</button>` : ''}<button class="btn" data-a="filter" data-cat="${esc(k.id)}">See activity</button>${editButton(k)}</div>`;
  // What was moved this month, each entry (several per transfer when done in parts).
  const moved = `<h2>Moved this month</h2>${txs.length ? list(txs) : `<p class="empty">${esc(NOTHING_MOVED)}</p>`}`;
  if (!k.itemized) {
    // One amount: Done for the category as a whole, and "Moved $300 of $300" with its entries.
    const open = k.budgetCents > 0 && k.spentCents < k.budgetCents;
    const acts = `${open ? doneButtons(null, k.id) : `<button class="btn" data-a="item-done-other" data-cat="${esc(k.id)}">Different amount</button>`}${k.spentCents > 0 ? notDoneButton(null, k.id) : ''}`;
    return `${head}${actions}
  <h2>${esc(movedLine(k))}</h2>
  <div class="acts">${acts}</div>
  ${txs.length ? list(txs) : `<p class="empty">${esc(NOTHING_MOVED)}</p>`}`;
  }
  const all = categoryItems(d, k.id);
  // A transfer planned at $0 (paused) is tucked away until it has an amount again.
  const paused = all.filter((i) => i.month?.status === 'not-this-month' && i.amountCents === 0);
  const items = all.filter((i) => !paused.includes(i));
  const archived = d.items.filter((i) => i.categoryId === k.id && i.archived);
  return `${head}
  ${moveCard(d.moveMoney.items.filter((m) => m.categoryId === k.id), { snooze: false })}
  ${actions}
  <h2>Transfers</h2>
  ${items.length ? `<div class="list">${items.map(transferRow).join('')}</div>` : '<p class="empty">No transfers yet. Add the money you move each month, like a Roth IRA or travel savings.</p>'}
  ${paused.length ? `<details class="more"><summary>At $0 for now (${paused.length})</summary><div class="list">${paused.map(transferRow).join('')}</div></details>` : ''}
  ${archived.length ? `<details class="more"><summary>Archived (${archived.length})</summary><div class="list">${archived.map(transferRow).join('')}</div></details>` : ''}
  ${moved}`;
}

/** "Removed $300 from Oct 15": the undo toast after Mark not done. */
export const removedText = (r: { amountCents: number; date: string }) => `Removed ${money(r.amountCents, r.amountCents % 100 ? 2 : 0)} from ${shortDate(r.date)}`;

/** "Moved $300 of $300": a plain category of money set aside, this month. */
export const movedLine = (k: CategoryDto) => `Moved ${money(k.spentCents)} of ${money(k.budgetCents)}`;

/**
 * "Done for a different amount": the amount and the day the money moved (this month, up to today),
 * for a transfer item or a plain category of money set aside.
 */
export function vDoneSheet(d: OverviewDto, target: ItemDto | CategoryDto): string {
  const { key, today } = d.month;
  // Prefilled with what is left to move (or the plan once it is done).
  let amount: number;
  if ('categoryId' in target) {
    const m = target.month;
    const left = m ? Math.max(0, m.expectedCents - (m.paidCents ?? 0)) : 0;
    amount = left || m?.expectedCents || target.amountCents;
  } else amount = Math.max(0, target.budgetCents - target.spentCents) || target.budgetCents;
  const field = 'categoryId' in target ? `<input type="hidden" id="d-id" value="${esc(target.id)}">` : `<input type="hidden" id="d-cat" value="${esc(target.id)}">`;
  // A transfer item that is split (70/30): the amount moved is the plan / my percent ($140 at 70% is $200).
  const plan = amount;
  const split = 'categoryId' in target && target.splitPercent != null && splitsEnabled(d.partner) && !target.spread;
  if (split) amount = grossFor(plan, target.splitPercent!);
  const splitBox = 'categoryId' in target ? doneSplitBlock(d, target, plan, amount) : '';
  return `<div class="grab"></div><h2>${esc(target.name)} <small>${split ? 'done' : 'done for a different amount'}</small></h2>
  ${field}
  <label class="fld"><span>Amount moved</span><div class="amt"><b>$</b>${centsField('d-amt', amount)}</div></label>
  <label class="fld"><span>Date</span><input id="d-date" type="date" min="${isoDay(key, 1)}" max="${isoDay(key, Math.max(1, today))}" value="${isoDay(key, Math.max(1, today))}"></label>
  ${splitBox}
  <p class="err" id="f-err"></p>
  <div class="btns"><button class="btn primary" data-a="item-done-save">Done</button><button class="btn" data-a="close">Cancel</button></div>`;
}

/**
 * "Yes, it renewed": the amount it charged (the annual amount to start) and the day. Saving logs
 * it, linked to the item, which starts the next term.
 */
export function vRenewedSheet(d: OverviewDto, item: ItemDto): string {
  const today = isoDay(d.month.key, Math.max(1, d.month.today));
  return `<div class="grab"></div><h2>${esc(item.name)} <small>renewed</small></h2>
  <input type="hidden" id="d-id" value="${esc(item.id)}">
  <label class="fld"><span>Amount charged</span><div class="amt"><b>$</b>${centsField('d-amt', item.amountCents)}</div></label>
  <label class="fld"><span>Date</span><input id="d-date" type="date" max="${today}" value="${item.renewsOn && item.renewsOn <= today ? item.renewsOn : today}"></label>
  <p class="err" id="f-err"></p>
  <div class="btns"><button class="btn primary" data-a="renewed-save">Save</button><button class="btn" data-a="close">Cancel</button></div>`;
}

/** Under the auto-log toggle. Variable items (last month's amount) and any-day items never log themselves. */
/** What auto-log does, for items and for "same amount every month" categories. */
export const AUTO_LOG_WHAT = 'Hush logs it at its amount on the due day, and swaps in the real alert if one comes later.';
export const AUTO_LOG_LABEL = 'Log this automatically on the due day if no alert arrives';
export const AUTO_LOG_NOTE = `${AUTO_LOG_WHAT} Monthly charges Hush found start with this on; items you add start with it off. Items that change every month, like electricity, are never logged automatically.`;

/** Under the Kind choice in a category of money set aside. */
export const TRANSFER_NOTE = 'Money set aside is planned as transfers you make yourself. Tap Done when the money has moved; Hush never matches it to card alerts.';

/** Under the auto-log switch of a spread item. */
export const SPREAD_AUTO_LOG_NOTE = 'A yearly item spread over 12 months is never logged automatically. Its monthly share counts on its own.';
/** Said before saving when an item with auto-log on is switched to spread. */
export const AUTO_LOG_OFF_NOTE = 'Auto-log turns off when you save: a yearly item spread over 12 months is never logged automatically.';

/** The editor's yearly answers, read back by main.ts for the live summary and the save. */
export interface YearlyAnswers {
  amountCents: number;
  lastPaidOn: string | null;
  renewsOn: string | null;
  renews: boolean;
  spread: boolean;
  endsMonth: string | null;
}

/** The summary line under the yearly questions, for today. */
export const yearlySummaryText = (a: YearlyAnswers, today: string) => yearlySummary({ ...a, today });

/**
 * A yearly item: four questions in order (the yearly charge is the amount above), "When was it
 * last charged?", "Will it renew?" and "How do you count it?", then one live summary line. With no
 * last charge it asks "When does it renew?" instead. The renewal date is worked out from the last
 * charge (12 months on); the end month is only typed behind "Change the end month".
 */
function yearlyFields(
  item: ItemDto | null,
  v: { cadence: string; spread: boolean; amount: number | null; months: number[]; dueDay: number | null; lastPaidOn: string; renews: boolean; endsMonth: string },
  today: string,
): string {
  const renewsOn = item?.renewsOn ?? (v.months[0] ? nextOccurrence(v.months[0], v.dueDay, today) : '');
  const answers: YearlyAnswers = { amountCents: v.amount ?? 0, lastPaidOn: v.lastPaidOn || null, renewsOn: renewsOn || null, renews: v.renews, spread: v.spread, endsMonth: v.endsMonth || null };
  return `<div id="i-yearly" ${v.cadence === 'yearly' ? '' : 'hidden'}>
  <label class="fld"><span>When was it last charged? (optional)</span><input id="i-lastpaid" type="date" max="${today}" value="${esc(v.lastPaidOn)}"></label>
  <label class="fld" id="i-renewson-f" ${v.lastPaidOn ? 'hidden' : ''}><span>When does it renew?</span><input id="i-renewson" type="date" value="${esc(renewsOn)}" data-orig="${esc(renewsOn)}" data-any="${v.dueDay === null ? '1' : ''}"></label>
  <label class="fld"><span>Will it renew?</span><select id="i-renews" class="sel"><option value="1" ${v.renews ? 'selected' : ''}>Yes</option><option value="0" ${v.renews ? '' : 'selected'}>No</option></select></label>
  <div class="fld"><span>How do you count it?</span>
  <label class="check"><input type="radio" name="i-count" id="i-count-whole" value="whole" ${v.spread ? '' : 'checked'}> The whole amount in the renewal month</label>
  <label class="check"><input type="radio" name="i-count" id="i-count-spread" value="spread" ${v.spread ? 'checked' : ''}> Spread over 12 months</label></div>
  <p class="hint ysum" id="i-summary" data-today="${today}">${esc(yearlySummaryText(answers, today))}</p>
  <button class="linkbtn" data-a="item-end-toggle" ${v.endsMonth ? 'hidden' : ''}>Change the end month</button>
  <label class="fld" id="i-ends-f" ${v.endsMonth ? '' : 'hidden'}><span>Last month it counts</span><input id="i-ends" type="month" value="${esc(v.endsMonth)}"></label>
  </div>`;
}

/** The item editor. `prefill` comes from "Make this a monthly item". */
export function vItemSheet(
  d: OverviewDto,
  item: ItemDto | null,
  categoryId: string,
  prefill: { name?: string; amountCents?: number; dueDay?: number | null; fromTransactionId?: string } = {},
): string {
  const v = {
    name: item?.name ?? prefill.name ?? '',
    amount: item?.amountCents ?? prefill.amountCents ?? null,
    lastMonth: item?.expectedMode === 'last_month',
    dueDay: item ? item.dueDay : (prefill.dueDay ?? null),
    cadence: item?.cadence ?? 'monthly',
    autoLog: item?.autoLog ?? false,
    months: item?.months ?? [],
    note: item?.note ?? '',
    spread: item?.spread ?? false,
    lastPaidOn: item?.lastPaidOn ?? '',
    renews: item?.renews ?? true,
    endsMonth: item?.endsMonth ?? '',
  };
  const cat = d.categories.find((k) => k.id === categoryId);
  const transfer = cat?.kind === 'set_aside' || item?.kind === 'transfer';
  // Yearly items get their own questions; Due and the month chips are for the other repeats.
  const yearly = !transfer && v.cadence === 'yearly';
  const today = isoDay(d.month.key, Math.max(1, d.month.today));
  const canLog = !v.lastMonth && v.dueDay !== null;
  const days = `<option value="">Any day</option>${Array.from({ length: 31 }, (_, i) => i + 1)
    .map((n) => `<option value="${n}" ${v.dueDay === n ? 'selected' : ''}>Day ${n}</option>`)
    .join('')}`;
  const chips = MONTHS.map(
    (m, i) => `<button class="chip" data-a="item-month" data-m="${i + 1}" aria-pressed="${v.months.includes(i + 1)}">${m}</button>`,
  ).join('');
  // Money set aside holds transfers only: the Kind choice shows it, and the bill-only fields go.
  const kind = transfer
    ? `<label class="fld"><span>Kind</span><select id="i-kind-pick" class="sel"><option value="transfer" selected>Transfer I make myself</option><option value="expense" disabled>Bill or purchase</option></select></label>
  <p class="hint">${esc(TRANSFER_NOTE)}</p>`
    : '';
  return `<div class="grab"></div><div class="sheethead"><h2>${item ? (transfer ? 'Edit transfer' : 'Edit item') : transfer ? 'New transfer' : 'New item'}${cat ? ` <small>in ${esc(cat.name)}</small>` : ''}</h2><button class="btn sheetclose" data-a="close">Close</button></div>
  <input type="hidden" id="i-cat" value="${esc(categoryId)}"><input type="hidden" id="i-id" value="${esc(item?.id ?? '')}"><input type="hidden" id="i-from" value="${esc(prefill.fromTransactionId ?? '')}"><input type="hidden" id="i-kind" value="${transfer ? 'transfer' : 'expense'}">
  ${kind}
  <label class="fld"><span>Name</span><input id="i-name" type="text" maxlength="60" placeholder="${transfer ? 'Roth IRA, Travel savings' : 'Netflix, Electricity, Rent'}" value="${esc(v.name)}"></label>
  <label class="fld"><span>Repeats</span><select id="i-cadence" class="sel">
    <option value="monthly" ${v.cadence === 'monthly' ? 'selected' : ''}>Every month</option>
    <option value="every_n_months" ${v.cadence === 'every_n_months' ? 'selected' : ''}>Every few months</option>
    <option value="yearly" ${v.cadence === 'yearly' ? 'selected' : ''}>Once a year</option></select></label>
  <label class="fld"><span id="i-amt-label">${transfer ? 'Planned amount' : yearly ? 'How much is the yearly charge?' : 'Amount'}</span><div class="amt"><b>$</b><input id="i-amt" inputmode="decimal" placeholder="0.00" value="${v.amount !== null ? (v.amount / 100).toFixed(2) : ''}"></div></label>
  ${transfer ? '' : `<label class="check" id="i-last-f" ${yearly ? 'hidden' : ''}><input type="checkbox" id="i-last" ${v.lastMonth ? 'checked' : ''}> Use last month's amount</label>`}
  ${transfer ? '' : yearlyFields(item, v, today)}
  <label class="fld" id="i-day-f" ${yearly ? 'hidden' : ''}><span>Due</span><select id="i-day" class="sel">${days}</select></label>
  <div class="fld" id="i-months" ${v.cadence !== 'every_n_months' ? 'hidden' : ''}><span>Months it is paid</span><div class="chips">${chips}</div></div>
  ${
    transfer
      ? ''
      : `<label class="check"><input type="checkbox" id="i-auto" ${v.autoLog && canLog && !v.spread ? 'checked' : ''} ${canLog && !v.spread ? '' : 'disabled'}> Log this automatically on the due day if no alert arrives</label>
  <p class="hint" id="i-auto-note">${esc(v.spread ? SPREAD_AUTO_LOG_NOTE : AUTO_LOG_NOTE)}</p>`
  }
  <label class="fld"><span>Note</span><input id="i-note" type="text" maxlength="200" placeholder="${transfer ? 'Into the Vanguard account' : 'Company pays $100'}" value="${esc(v.note)}"></label>
  <p class="err" id="f-err"></p>
  <div class="btns"><button class="btn primary" data-a="item-save">${item ? 'Save changes' : transfer ? 'Add transfer' : 'Add item'}</button>${
    item
      ? `<button class="btn" data-a="item-archive" data-id="${esc(item.id)}" data-archived="${item.archived}">${item.archived ? 'Restore' : 'Archive'}</button><button class="btn danger" data-a="item-delete" data-id="${esc(item.id)}">Delete</button>`
      : ''
  }</div>`;
}

/** The Item picker in the edit sheet: the category's items, plus None. */
export function itemPicker(d: OverviewDto, categoryId: string | null, selected: string | null): string {
  const k = d.categories.find((x) => x.id === categoryId);
  // Transfers are marked Done on their item, never picked for a purchase.
  if (!k?.itemized || k.kind === 'set_aside') return '';
  const items = categoryItems(d, k.id).filter((i) => i.kind !== 'transfer');
  if (!items.length) return '';
  return `<div class="fld" id="f-items"><span>Item</span><div class="chips">${[
    `<button class="chip" data-a="pick-item" data-item="" aria-pressed="${!selected}">None</button>`,
    ...items.map((i) => `<button class="chip" data-a="pick-item" data-item="${esc(i.id)}" aria-pressed="${selected === i.id}">${esc(i.name)}</button>`),
  ].join('')}</div></div>`;
}
